import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import type { MoleculeModel } from '@chemai/engine';
import { elementBySymbol } from '@chemai/data';
import { useI18n } from '../i18n';
import { type Mat3, type Vec3, apply, dragRotate, project, rotationX, rotationY, multiply } from '../lib/projection';

/**
 * Dependency-free 3D ball-and-stick renderer on <canvas>.
 * Painter's algorithm with depth-sorted atoms, bond halves and lone-pair lobes; works on low-end
 * Android devices without WebGL. Interaction: pointer drag, wheel/pinch zoom, keyboard, buttons.
 */

const CPK: Record<string, string> = {
  H: '#f4f4f4', C: '#3a3f47', N: '#3b5bdb', O: '#e03131', F: '#7fd36a', Cl: '#2fb344', Br: '#a8452f',
  I: '#7b2d8b', S: '#f0c419', P: '#f08c00', B: '#f4a4a4', Xe: '#3fa7b5', Be: '#a9d02f',
  A: '#5c6f91', X: '#2fa39a',
};
const colorOf = (s: string) => CPK[s] ?? '#d26ad2';

function shade(hex: string, f: number): string {
  const n = parseInt(hex.slice(1), 16);
  const ch = (v: number) => Math.max(0, Math.min(255, Math.round(f < 0 ? v * (1 + f) : v + (255 - v) * f)));
  return `rgb(${ch(n >> 16)}, ${ch((n >> 8) & 255)}, ${ch(n & 255)})`;
}

function luminance(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  return (0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
}

function atomRadius(sym: string): number {
  const r = elementBySymbol(sym)?.covalentRadius ?? 100;
  return 0.18 + (r / 100) * 0.22; // model units (Å), deliberately smaller than real
}

const INITIAL: Mat3 = multiply(rotationX(-0.35), rotationY(0.55));

interface Props {
  model: MoleculeModel;
  showLabels?: boolean;
  showLonePairs?: boolean;
  autoRotate?: boolean;
  height?: number;
  description: string;
}

export function MoleculeViewer({ model, showLabels = true, showLonePairs = true, autoRotate = false, height = 360, description }: Props) {
  const { t } = useI18n();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rot = useRef<Mat3>(INITIAL);
  const zoom = useRef(1);
  const [, force] = useState(0);
  const redraw = useCallback(() => force((n) => n + 1), []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const extent = Math.max(1.2, ...model.atoms.map((a) => Math.hypot(...a.position) + atomRadius(a.symbol)));
    const ppu = (Math.min(w, h) / (2 * extent * 1.25)) * zoom.current;
    const cx = w / 2;
    const cy = h / 2;
    const P = model.atoms.map((a) => project(apply(rot.current, a.position as Vec3), cx, cy, ppu));

    type Item = { z: number; paint: () => void };
    const items: Item[] = [];

    // Bonds: each bond split into two halves coloured by their atom; multiple bonds offset in screen space.
    for (const b of model.bonds) {
      const A = P[b.a];
      const B = P[b.b];
      const dx = B.x - A.x;
      const dy = B.y - A.y;
      const len = Math.hypot(dx, dy) || 1;
      const nx = -dy / len;
      const ny = dx / len;
      const lines = b.order >= 3 ? [-1, 0, 1] : b.order >= 1.5 ? [-0.5, 0.5] : [0];
      const width = Math.max(2, ppu * 0.12);
      const gap = width * 1.6;
      lines.forEach((o, li) => {
        const dashed = b.order === 1.5 && li === 1;
        const ox = nx * o * gap;
        const oy = ny * o * gap;
        const mx = (A.x + B.x) / 2 + ox;
        const my = (A.y + B.y) / 2 + oy;
        for (const [S, atomIdx] of [[A, b.a], [B, b.b]] as const) {
          const z = (S.z + (A.z + B.z) / 2) / 2 - 0.001;
          items.push({
            z,
            paint: () => {
              ctx.save();
              ctx.lineCap = 'round';
              ctx.lineWidth = (width + 2) * S.scale;
              ctx.strokeStyle = 'rgba(0,0,0,0.35)';
              if (dashed) ctx.setLineDash([width, width * 1.2]);
              ctx.beginPath();
              ctx.moveTo(S.x + ox, S.y + oy);
              ctx.lineTo(mx, my);
              ctx.stroke();
              ctx.lineWidth = width * S.scale;
              ctx.strokeStyle = shade(colorOf(model.atoms[atomIdx].symbol), -0.05);
              ctx.stroke();
              ctx.restore();
            },
          });
        }
      });
    }

    // Lone pairs: translucent lobes with two electron dots.
    if (showLonePairs) {
      for (const lp of model.lonePairs) {
        const center = model.atoms[lp.atom].position;
        const d = lp.direction;
        const tip: Vec3 = [center[0] + d[0] * 0.75, center[1] + d[1] * 0.75, center[2] + d[2] * 0.75];
        const Q = project(apply(rot.current, tip), cx, cy, ppu);
        items.push({
          z: Q.z,
          paint: () => {
            const r = ppu * 0.32 * Q.scale;
            const g = ctx.createRadialGradient(Q.x, Q.y, r * 0.1, Q.x, Q.y, r);
            g.addColorStop(0, 'rgba(250, 176, 5, 0.55)');
            g.addColorStop(1, 'rgba(250, 176, 5, 0.05)');
            ctx.fillStyle = g;
            ctx.beginPath();
            ctx.arc(Q.x, Q.y, r, 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = 'rgba(120, 70, 0, 0.9)';
            for (const s of [-1, 1]) {
              ctx.beginPath();
              ctx.arc(Q.x + s * r * 0.25, Q.y, Math.max(1.5, r * 0.09), 0, Math.PI * 2);
              ctx.fill();
            }
          },
        });
      }
    }

    // Atoms as shaded spheres.
    model.atoms.forEach((a, i) => {
      const p = P[i];
      items.push({
        z: p.z,
        paint: () => {
          const r = atomRadius(a.symbol) * ppu * p.scale;
          const base = colorOf(a.symbol);
          const g = ctx.createRadialGradient(p.x - r * 0.35, p.y - r * 0.35, r * 0.1, p.x, p.y, r);
          g.addColorStop(0, shade(base, 0.6));
          g.addColorStop(0.5, base);
          g.addColorStop(1, shade(base, -0.45));
          ctx.fillStyle = g;
          ctx.beginPath();
          ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
          ctx.fill();
          ctx.lineWidth = 1;
          ctx.strokeStyle = 'rgba(0,0,0,0.35)';
          ctx.stroke();
          if (showLabels && r > 7) {
            ctx.fillStyle = luminance(base) > 0.6 ? '#1b1f27' : '#ffffff';
            ctx.font = `600 ${Math.max(10, Math.min(18, r * 0.85))}px system-ui, sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(a.symbol, p.x, p.y + 0.5);
          }
        },
      });
    });

    items.sort((x, y) => x.z - y.z).forEach((it) => it.paint());
  }, [model, showLabels, showLonePairs]);

  // redraw on every state change
  useEffect(() => { draw(); });

  // resize
  useEffect(() => {
    const c = canvasRef.current;
    if (!c || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => draw());
    ro.observe(c);
    return () => ro.disconnect();
  }, [draw]);

  // auto-rotation (respects reduced-motion)
  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!autoRotate || reduce) return;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      rot.current = multiply(rotationY(((now - last) / 1000) * 0.6), rot.current);
      last = now;
      draw();
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [autoRotate, draw]);

  // reset view when the molecule changes
  useEffect(() => { rot.current = INITIAL; zoom.current = 1; redraw(); }, [model.id, redraw]);

  // pointer + pinch
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const pointers = new Map<number, { x: number; y: number }>();
    let pinch = 0;
    const down = (e: PointerEvent) => { c.setPointerCapture(e.pointerId); pointers.set(e.pointerId, { x: e.clientX, y: e.clientY }); };
    const move = (e: PointerEvent) => {
      const prev = pointers.get(e.pointerId);
      if (!prev) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        if (pinch) zoom.current = Math.min(3, Math.max(0.4, zoom.current * (d / pinch)));
        pinch = d;
      } else {
        rot.current = dragRotate(rot.current, e.clientX - prev.x, e.clientY - prev.y);
      }
      draw();
    };
    const up = (e: PointerEvent) => { pointers.delete(e.pointerId); pinch = 0; };
    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      zoom.current = Math.min(3, Math.max(0.4, zoom.current * (e.deltaY < 0 ? 1.1 : 0.9)));
      draw();
    };
    c.addEventListener('pointerdown', down);
    c.addEventListener('pointermove', move);
    c.addEventListener('pointerup', up);
    c.addEventListener('pointercancel', up);
    c.addEventListener('wheel', wheel, { passive: false });
    return () => {
      c.removeEventListener('pointerdown', down);
      c.removeEventListener('pointermove', move);
      c.removeEventListener('pointerup', up);
      c.removeEventListener('pointercancel', up);
      c.removeEventListener('wheel', wheel);
    };
  }, [draw]);

  const act = (fn: () => void) => () => { fn(); draw(); };
  const left = act(() => { rot.current = multiply(rotationY(-0.26), rot.current); });
  const right = act(() => { rot.current = multiply(rotationY(0.26), rot.current); });
  const upR = act(() => { rot.current = multiply(rotationX(-0.26), rot.current); });
  const downR = act(() => { rot.current = multiply(rotationX(0.26), rot.current); });
  const zin = act(() => { zoom.current = Math.min(3, zoom.current * 1.15); });
  const zout = act(() => { zoom.current = Math.max(0.4, zoom.current / 1.15); });
  const reset = act(() => { rot.current = INITIAL; zoom.current = 1; });

  const onKey = (e: KeyboardEvent) => {
    const map: Record<string, () => void> = {
      ArrowLeft: left, ArrowRight: right, ArrowUp: upR, ArrowDown: downR, '+': zin, '=': zin, '-': zout, '0': reset,
    };
    const fn = map[e.key];
    if (fn) { e.preventDefault(); fn(); }
  };

  return (
    <div className="viewer">
      <canvas
        ref={canvasRef}
        className="viewer-canvas"
        style={{ height }}
        tabIndex={0}
        role="img"
        aria-label={description}
        onKeyDown={onKey}
      />
      <div className="viewer-controls" role="toolbar" aria-label={t('mol.title')}>
        <button type="button" className="icon-btn" onClick={left} aria-label={t('mol.rotateLeft')} title={t('mol.rotateLeft')}>⟲</button>
        <button type="button" className="icon-btn" onClick={right} aria-label={t('mol.rotateRight')} title={t('mol.rotateRight')}>⟳</button>
        <button type="button" className="icon-btn" onClick={upR} aria-label={t('mol.rotateUp')} title={t('mol.rotateUp')}>⤒</button>
        <button type="button" className="icon-btn" onClick={zin} aria-label={t('mol.zoomIn')} title={t('mol.zoomIn')}>+</button>
        <button type="button" className="icon-btn" onClick={zout} aria-label={t('mol.zoomOut')} title={t('mol.zoomOut')}>−</button>
        <button type="button" className="icon-btn" onClick={reset} aria-label={t('mol.resetView')} title={t('mol.resetView')}>⌂</button>
      </div>
    </div>
  );
}
